import {
  makeStyles,
  Theme,
  createStyles,
  Box,
  Typography,
  SvgIcon,
  ListItemIcon,
  Button,
} from "@material-ui/core";
import clsx from "clsx";
import { SVGProps } from "react";

type MenuItem = {
  title: string;
  icon: (props: SVGProps<SVGSVGElement>) => JSX.Element;
  content: JSX.Element;
  actionLabel: string;
  action: () => void;
  status: "error" | "warning" | "success";
};

export type NotificationItemProps = {
  menuItem: MenuItem;
  hasBorder?: boolean;
};

export const NotificationItem = ({ menuItem, hasBorder }: NotificationItemProps) => {
  const classes = useStyles();

  return (
    <Box
      onClick={menuItem.action}
      key={`menuItem-${menuItem.title}`}
      className={clsx(classes.notificationMenuItem, {
        [classes.menuItemBorder]: hasBorder,
      })}
    >
      <Box display="flex" alignItems="center">
        <ListItemIcon
          className={`${classes.icon} ${
            classes[`${menuItem.status}Icon` as "successIcon" | "warningIcon" | "errorIcon"]
          }`}
        >
          {menuItem?.icon ? <SvgIcon component={menuItem.icon} /> : null}
        </ListItemIcon>
        <Typography component="h1">{menuItem.title}</Typography>
      </Box>
      <Box paddingLeft="32px">
        <Typography component="p">{menuItem.content}</Typography>
        <Button onClick={menuItem.action}>{menuItem.actionLabel}</Button>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    notificationMenuItem: {
      padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,

      "& svg": {
        fontSize: 24,
      },

      "& h1": {
        fontSize: 12,
        fontWeight: "bold",
        lineHeight: 1.6,
        letterSpacing: 1,
        textTransform: "uppercase",
      },

      "& p": {
        fontSize: 12,
        fontWeight: "normal",
        lineHeight: 1.6,
        letterSpacing: 0,
      },

      "& button": {
        color: theme.palette.primary.main,
        fontSize: 12,
        fontWeight: "bold",
        lineHeight: 1.6,
        letterSpacing: 1,
        textTransform: "uppercase",
        padding: 0,
        borderRadius: 0,

        "&:hover": {
          background: "transparent",
          boxShadow: `0 1px 0 0 ${theme.palette.primary.main}`,
        },
        "&:active": {
          color: theme.palette.primary.dark,
          boxShadow: `0 1px 0 0 ${theme.palette.primary.dark}`,
        },
      },
    },
    menuItemBorder: {
      borderTop: `1px solid ${theme.palette.background.default}`,
    },
    icon: {
      marginRight: theme.spacing(1.25),
      minWidth: theme.spacing(3),

      color: theme.palette.coolGrey.main,
    },
    successIcon: {
      color: theme.palette.success.main,
    },
    errorIcon: {
      color: theme.palette.error.main,
    },
    warningIcon: {
      color: theme.palette.warning.main,
    },
  }),
);
